import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
  Input,
  Label,
  Button
} from 'reactstrap'
import BreadCrumb from 'Components/Common/BreadCrumb'
import Pagination from 'Components/Common/Pagination'
import { fetchSalesRecords } from 'helpers/backend_helper'
import { toast } from 'react-toastify'
import { PERPAGE_DEFAULT } from 'lib/constant'
import ReactApexChart from 'react-apexcharts'

// Seçilen arka plan URL'sini admin'de gösterilebilir tam adrese çevirir.
// Mutlak/data URL ise olduğu gibi; göreli ise backend origin'i (REACT_APP_API_URL'den
// /api/v1 atılarak) ön ekle.
const resolveMediaUrl = (u?: string | null): string => {
  if (!u) return ''
  if (/^https?:\/\//.test(u) || u.startsWith('data:')) return u
  const base = (process.env.REACT_APP_API_URL || '')
    .replace(/\/api\/v1\/?$/, '')
    .replace(/\/$/, '')
  return base + (u.startsWith('/') ? u : '/' + u)
}

interface IOrderSummary {
  orderCode?: string
  totalAmount?: number
  status?: string
  createdAt?: string
  photoCount?: number
  categoryId?: {
    name?: any
  }
  promoCode?: string
  promoDiscountType?: number // 1=Percentage, 2=Amount
  promoDiscountValue?: number
  activeActionId?: {
    name?: string
    label?: string
  }
  selectedBackgrounds?: string[] // Çekimde seçilen arka plan URL'leri (thumbnail)
}

interface IPhotoBoothInfo {
  _id: string
  name?: string
  serialNumber?: string
  location?: {
    city?: string
    address?: string
  }
}

interface ISalesSummary {
  count: number
  totalAmount: number
  cashAmount: number
  cardAmount: number
  cashCount: number
  cardCount: number
}

interface IBoothSale {
  name: string
  amount: number
  count: number
}

interface IProductInfo {
  _id: string
  name?: string
  price?: number
}

export interface ISaleRecord {
  _id: string
  orderId?: IOrderSummary
  photoBoothId?: IPhotoBoothInfo
  products?: IProductInfo[]
  price?: number
  totalAmount?: number
  paymentType?: string
  status?: string
  currency?: string
  createdAt?: string
}

const statusColorMap: Record<string, string> = {
  paid: 'success',
  failed: 'danger',
  cancelled: 'secondary',
  pending: 'warning',
  payment_pending: 'warning',
  payment_processing: 'info'
}

const formatCurrency = (value: number, currency?: string) => {
  const code = currency && typeof currency === 'string' ? currency.toUpperCase() : 'TRY'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2
    }).format(value)
  } catch {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2 }) + ` ${code}`
  }
}

const getCategoryName = (categoryId: any): string => {
  if (!categoryId) return '-'
  if (typeof categoryId.name === 'string') return categoryId.name
  if (Array.isArray(categoryId.name) && categoryId.name.length > 0) {
    return categoryId.name[0]?.text || '-'
  }
  return '-'
}

const Sales = () => {
  document.title = 'Sales | SocialBooth - Admin Dashboard'

  const [sales, setSales] = useState<ISaleRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [summary, setSummary] = useState<ISalesSummary>({
    count: 0, totalAmount: 0, cashAmount: 0, cardAmount: 0, cashCount: 0, cardCount: 0
  })
  const [boothSales, setBoothSales] = useState<IBoothSale[]>([])
  const [showAllBooths, setShowAllBooths] = useState(false)

  // Filter states
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('week')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')

  const getDateRange = () => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (dateFilter) {
      case 'today':
        return {
          startDate: today.toISOString(),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
        }
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
        return {
          startDate: weekAgo.toISOString(),
          endDate: new Date().toISOString()
        }
      case 'month':
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
        return {
          startDate: monthAgo.toISOString(),
          endDate: new Date().toISOString()
        }
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            startDate: new Date(customStartDate).toISOString(),
            endDate: new Date(customEndDate + ' 23:59:59').toISOString()
          }
        }
        return {}
      default:
        return {}
    }
  }

  const totals = useMemo(() => {
    const currency = sales[0]?.currency || 'TRY'

    return {
      count: summary.count,
      amount: summary.totalAmount,
      cashAmount: summary.cashAmount,
      cardAmount: summary.cardAmount,
      cashCount: summary.cashCount,
      cardCount: summary.cardCount,
      currency
    }
  }, [sales, summary])

  const loadSales = useCallback(
    async (page: number) => {
      setLoading(true)
      try {
        const dateRange = getDateRange()
        const params: any = { page, limit: PERPAGE_DEFAULT }

        if (paymentTypeFilter !== 'all') {
          params.paymentType = paymentTypeFilter
        }

        if (dateRange.startDate) params.startDate = dateRange.startDate
        if (dateRange.endDate) params.endDate = dateRange.endDate

        const raw: any = await fetchSalesRecords(params)
        // axios interceptor returns response.data, but check both cases
        const body = raw?.ok !== undefined ? raw : raw?.data ?? raw
        const rows: ISaleRecord[] = body.data ?? []
        setSales(rows)
        setTotalPages(body.totalPages ?? 1)
        if (body.summary) {
          setSummary(body.summary)
        }
        if (body.boothSales) {
          setBoothSales(body.boothSales)
          setShowAllBooths(false)
        }
      } catch (error) {
        toast.error(error as string)
      } finally {
        setLoading(false)
      }
    },
    [paymentTypeFilter, dateFilter, customStartDate, customEndDate]
  )

  useEffect(() => {
    loadSales(currentPage)
  }, [currentPage, paymentTypeFilter, dateFilter, customStartDate, customEndDate, loadSales])

  const formatDate = (value?: string) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Aktif filtrelerle TÜM kayıtları çekip gerçek .xlsx olarak indirir.
  // CSV yerine xlsx: Türkçe Excel'de ayraç/karakter sorunu yaşanmaz.
  const handleExportExcel = async () => {
    setExporting(true)
    try {
      const dateRange = getDateRange()
      // Tek istekte tüm filtreli kayıtlar (backend limit'i destekliyor)
      const params: any = { page: 1, limit: 50000 }
      if (paymentTypeFilter !== 'all') params.paymentType = paymentTypeFilter
      if (dateRange.startDate) params.startDate = dateRange.startDate
      if (dateRange.endDate) params.endDate = dateRange.endDate

      const raw: any = await fetchSalesRecords(params)
      const body = raw?.ok !== undefined ? raw : raw?.data ?? raw
      const rows: ISaleRecord[] = body.data ?? []

      if (rows.length === 0) {
        toast.info('Dışa aktarılacak kayıt bulunamadı')
        return
      }

      const excelRows = rows.map((sale, index) => {
        const amount = sale.totalAmount ?? sale.price ?? 0
        const loc = sale.photoBoothId?.location
        const categoryName =
          getCategoryName(sale.orderId?.categoryId) !== '-'
            ? getCategoryName(sale.orderId?.categoryId)
            : sale.orderId?.activeActionId?.name ||
              sale.orderId?.activeActionId?.label ||
              '-'
        const paymentTypeLabel =
          sale.paymentType === 'creditCard'
            ? 'Kredi Kartı'
            : sale.paymentType === 'cash'
              ? 'Nakit'
              : sale.paymentType === 'promotionCode'
                ? 'Promosyon Kodu'
                : sale.paymentType || '-'

        return {
          '#': index + 1,
          'Tarih': formatDate(sale.createdAt || sale.orderId?.createdAt),
          'Kabin': sale.photoBoothId?.name || '-',
          'Lokasyon': loc
            ? [loc.city, loc.address].filter(Boolean).join(', ') || '-'
            : '-',
          'Hizmet': categoryName,
          'Fotoğraf Adedi': sale.orderId?.photoCount || sale.products?.length || 0,
          'Tutar': amount,
          'Para Birimi': (sale.currency || 'TRY').toUpperCase(),
          'Ödeme Tipi': paymentTypeLabel,
          'Promosyon Kodu': sale.orderId?.promoCode || '',
          'İndirim': sale.orderId?.promoDiscountValue
            ? sale.orderId?.promoDiscountType === 1
              ? `%${sale.orderId.promoDiscountValue}`
              : `${sale.orderId.promoDiscountValue} TL`
            : '',
          'Durum': sale.status || sale.orderId?.status || '-',
          'Sipariş Kodu': sale.orderId?.orderCode || ''
        }
      })

      // xlsx'i dinamik import et — ana bundle'ı şişirmesin
      const XLSX = await import('xlsx')
      const ws = XLSX.utils.json_to_sheet(excelRows)
      ws['!cols'] = [
        { wch: 5 },  // #
        { wch: 17 }, // Tarih
        { wch: 20 }, // Kabin
        { wch: 30 }, // Lokasyon
        { wch: 18 }, // Hizmet
        { wch: 14 }, // Fotoğraf Adedi
        { wch: 10 }, // Tutar
        { wch: 11 }, // Para Birimi
        { wch: 15 }, // Ödeme Tipi
        { wch: 15 }, // Promosyon Kodu
        { wch: 10 }, // İndirim
        { wch: 10 }, // Durum
        { wch: 14 }  // Sipariş Kodu
      ]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Satışlar')
      const stamp = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(wb, `socialbooth-satislar-${stamp}.xlsx`)
      toast.success(`${rows.length} kayıt Excel'e aktarıldı`)
    } catch (error) {
      console.error('[Sales] Excel export failed:', error)
      toast.error('Excel dışa aktarma başarısız oldu')
    } finally {
      setExporting(false)
    }
  }

  const renderStatus = (status?: string) => {
    if (!status) return '-'
    const normalized = status.toLowerCase()
    const color = statusColorMap[normalized] || 'primary'
    return (
      <Badge color={color} className='text-uppercase'>
        {status.replace(/_/g, ' ')}
      </Badge>
    )
  }

  // Chart data for payment type breakdown
  const pieChartOptions = {
    chart: {
      type: 'pie' as const,
      height: 250,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    labels: ['Cash', 'Credit Card'],
    colors: ['#0ab39c', '#405189'],
    legend: {
      position: 'bottom' as const
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
      dropShadow: { enabled: false },
      offset: 0
    },
    plotOptions: {
      pie: {
        dataLabels: {
          offset: -5,
          minAngleToShowLabel: 10
        }
      }
    }
  }

  const pieChartSeries = [totals.cashAmount, totals.cardAmount]

  // Top booth chart (backend'den gelen aggregated data)
  const TOP_N = 5
  const hasMoreBooths = boothSales.length > TOP_N

  const chartBooths = useMemo(() => {
    if (showAllBooths) return boothSales

    if (boothSales.length <= TOP_N) return boothSales

    const top = boothSales.slice(0, TOP_N)
    const rest = boothSales.slice(TOP_N)
    const othersAmount = rest.reduce((s, b) => s + b.amount, 0)
    const othersCount = rest.reduce((s, b) => s + b.count, 0)
    return [...top, { name: `Others (${rest.length})`, amount: othersAmount, count: othersCount }]
  }, [boothSales, showAllBooths])

  const MAX_CHART_HEIGHT = 400
  const chartHeight = showAllBooths ? Math.max(250, boothSales.length * 40) : 250
  const needsScroll = showAllBooths && chartHeight > MAX_CHART_HEIGHT

  const barChartOptions = {
    chart: {
      type: 'bar' as const,
      height: chartHeight,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        dataLabels: {
          position: 'top'
        }
      }
    },
    colors: ['#299cdb'],
    dataLabels: {
      enabled: true,
      formatter: (val: number) => formatCurrency(val, totals.currency),
      offsetX: 30,
      style: {
        fontSize: '12px',
        colors: ['#304758']
      }
    },
    xaxis: {
      categories: chartBooths.map((b) => b.name),
      labels: {
        formatter: (val: string) => formatCurrency(parseFloat(val), totals.currency)
      }
    },
    yaxis: {
      title: {
        text: 'Photo Booth'
      }
    }
  }

  const barChartSeries = [
    {
      name: 'Sales',
      data: chartBooths.map((b) => b.amount)
    }
  ]

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Sales' pageTitle='Sales' />

          {/* Summary Cards */}
          <Row className='mb-4'>
            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Total Sales</p>
                    </div>
                    <div className='flex-shrink-0'>
                      <h5 className='text-success fs-14 mb-0'>
                        <i className='ri-arrow-right-up-line fs-13 align-middle'></i> {totals.count}
                      </h5>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {formatCurrency(totals.amount, totals.currency)}
                      </h4>
                      <span className='badge badge-soft-success'>Records: {totals.count}</span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-success rounded fs-3'>
                        <i className='bx bx-lira text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Cash Payments</p>
                    </div>
                    <div className='flex-shrink-0'>
                      <h5 className='text-info fs-14 mb-0'>
                        <i className='ri-money-dollar-circle-line fs-13 align-middle'></i>{' '}
                        {totals.cashCount}
                      </h5>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {formatCurrency(totals.cashAmount, totals.currency)}
                      </h4>
                      <span className='badge badge-soft-info'>
                        {totals.amount > 0
                          ? ((totals.cashAmount / totals.amount) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-info rounded fs-3'>
                        <i className='bx bx-wallet text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Card Payments</p>
                    </div>
                    <div className='flex-shrink-0'>
                      <h5 className='text-primary fs-14 mb-0'>
                        <i className='ri-bank-card-line fs-13 align-middle'></i> {totals.cardCount}
                      </h5>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {formatCurrency(totals.cardAmount, totals.currency)}
                      </h4>
                      <span className='badge badge-soft-primary'>
                        {totals.amount > 0
                          ? ((totals.cardAmount / totals.amount) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-primary rounded fs-3'>
                        <i className='bx bx-credit-card text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Total Sales Count</p>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {totals.count}
                      </h4>
                      <span className='badge badge-soft-warning'>
                        {totals.cashCount} Cash / {totals.cardCount} Card
                      </span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-warning rounded fs-3'>
                        <i className='bx bx-receipt text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          {sales.length > 0 && (
            <Row className='mb-4'>
              <Col xl={6} className='d-flex'>
                <Card className='w-100'>
                  <CardHeader>
                    <h4 className='card-title mb-0'>Payment Type Breakdown</h4>
                  </CardHeader>
                  <CardBody className='d-flex align-items-center justify-content-center'>
                    <ReactApexChart
                      options={pieChartOptions}
                      series={pieChartSeries}
                      type='pie'
                      height={250}
                    />
                  </CardBody>
                </Card>
              </Col>

              <Col xl={6} className='d-flex'>
                <Card className='w-100'>
                  <CardHeader className='d-flex align-items-center justify-content-between'>
                    <h4 className='card-title mb-0'>Top Performing Booths</h4>
                    {hasMoreBooths && (
                      <Button
                        color='soft-primary'
                        size='sm'
                        onClick={() => setShowAllBooths(!showAllBooths)}
                      >
                        {showAllBooths ? (
                          <>
                            <i className='ri-collapse-diagonal-line me-1'></i>
                            Show Top {TOP_N}
                          </>
                        ) : (
                          <>
                            <i className='ri-expand-diagonal-line me-1'></i>
                            Show All ({boothSales.length})
                          </>
                        )}
                      </Button>
                    )}
                  </CardHeader>
                  <CardBody>
                    {chartBooths.length > 0 ? (
                      <div style={needsScroll ? { maxHeight: `${MAX_CHART_HEIGHT}px`, overflowY: 'auto' } : undefined}>
                        <ReactApexChart
                          key={showAllBooths ? 'all' : 'top'}
                          options={barChartOptions}
                          series={barChartSeries}
                          type='bar'
                          height={chartHeight}
                        />
                      </div>
                    ) : (
                      <div className='text-center py-4 text-muted'>No booth data available</div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Filters */}
          <Card className='mb-4'>
            <CardBody>
              <Row className='g-3'>
                <Col md={3}>
                  <Label>Date Range</Label>
                  <Input
                    type='select'
                    value={dateFilter}
                    onChange={(e) => {
                      setDateFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                  >
                    <option value='all'>All Time</option>
                    <option value='today'>Today</option>
                    <option value='week'>Last 7 Days</option>
                    <option value='month'>Last 30 Days</option>
                    <option value='custom'>Custom Range</option>
                  </Input>
                </Col>

                {dateFilter === 'custom' && (
                  <>
                    <Col md={3}>
                      <Label>Start Date</Label>
                      <Input
                        type='date'
                        value={customStartDate}
                        onChange={(e) => {
                          setCustomStartDate(e.target.value)
                          setCurrentPage(1)
                        }}
                      />
                    </Col>
                    <Col md={3}>
                      <Label>End Date</Label>
                      <Input
                        type='date'
                        value={customEndDate}
                        onChange={(e) => {
                          setCustomEndDate(e.target.value)
                          setCurrentPage(1)
                        }}
                      />
                    </Col>
                  </>
                )}

                <Col md={3}>
                  <Label>Payment Type</Label>
                  <Input
                    type='select'
                    value={paymentTypeFilter}
                    onChange={(e) => {
                      setPaymentTypeFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                  >
                    <option value='all'>All Types</option>
                    <option value='cash'>Cash</option>
                    <option value='creditCard'>Credit Card</option>
                    <option value='promotionCode'>Promotion Code</option>
                  </Input>
                </Col>

                <Col md={3} className='d-flex align-items-end'>
                  <Button
                    color='secondary'
                    outline
                    onClick={() => {
                      setDateFilter('all')
                      setPaymentTypeFilter('all')
                      setCustomStartDate('')
                      setCustomEndDate('')
                      setCurrentPage(1)
                    }}
                    className='w-100'
                  >
                    <i className='ri-refresh-line align-middle'></i> Reset Filters
                  </Button>
                </Col>

                <Col md={3} className='d-flex align-items-end'>
                  <Button
                    color='success'
                    onClick={handleExportExcel}
                    disabled={exporting || loading}
                    className='w-100'
                  >
                    {exporting ? (
                      <Spinner size='sm' className='me-1' />
                    ) : (
                      <i className='ri-file-excel-2-line align-middle me-1'></i>
                    )}
                    Export Excel
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Sales Table */}
          <Card>
            <CardBody>
              <div className='table-responsive'>
                <Table className='align-middle table-nowrap mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th>#</th>
                      <th>Booth Address</th>
                      <th>Photo Booth</th>
                      <th>Category</th>
                      <th>Background</th>
                      <th>Quantity</th>
                      <th>Amount</th>
                      <th>Payment Type</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={10} className='text-center py-4'>
                          <Spinner size='sm' className='me-2' /> Loading sales...
                        </td>
                      </tr>
                    )}

                    {!loading && sales.length === 0 && (
                      <tr>
                        <td colSpan={10} className='text-center py-4'>
                          <div className='text-muted'>
                            <i className='ri-inbox-line fs-2 d-block mb-2'></i>
                            No sales found. Try adjusting your filters.
                          </div>
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      sales.map((sale, index) => {
                        const amount = sale.totalAmount ?? sale.price ?? 0
                        const currency = sale.currency || 'TRY'
                        const boothName = sale.photoBoothId?.name || '-'
                        const loc = sale.photoBoothId?.location
                        const boothLocation = loc
                          ? [loc.city, loc.address].filter(Boolean).join(', ') || '-'
                          : '-'
                        const categoryName =
                          getCategoryName(sale.orderId?.categoryId) !== '-'
                            ? getCategoryName(sale.orderId?.categoryId)
                            : sale.orderId?.activeActionId?.name ||
                              sale.orderId?.activeActionId?.label ||
                              '-'
                        const photoCount = sale.orderId?.photoCount || sale.products?.length || 0

                        return (
                          <tr key={sale._id} className='animate-fade-in'>
                            <td>{(currentPage - 1) * PERPAGE_DEFAULT + index + 1}</td>
                            <td>
                              <span className='fw-medium text-dark'>{boothLocation}</span>
                            </td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <div className='avatar-xs me-2'>
                                  <span className='avatar-title rounded-circle bg-soft-primary text-primary'>
                                    <i className='ri-camera-line'></i>
                                  </span>
                                </div>
                                <span>{boothName}</span>
                              </div>
                            </td>
                            <td>{categoryName}</td>
                            <td>
                              {sale.orderId?.selectedBackgrounds &&
                              sale.orderId.selectedBackgrounds.length > 0 ? (
                                <div className='d-flex flex-wrap gap-1'>
                                  {sale.orderId.selectedBackgrounds
                                    .slice(0, 4)
                                    .map((bg, bi) => (
                                      <img
                                        key={bi}
                                        src={resolveMediaUrl(bg)}
                                        alt='bg'
                                        title={bg}
                                        style={{
                                          width: 34,
                                          height: 34,
                                          objectFit: 'cover',
                                          borderRadius: 6,
                                          border: '1px solid #ddd',
                                        }}
                                      />
                                    ))}
                                </div>
                              ) : (
                                <span className='text-muted'>-</span>
                              )}
                            </td>
                            <td>
                              <span className='badge bg-secondary text-white'>
                                {photoCount} photos
                              </span>
                            </td>
                            <td className='fw-semibold'>{formatCurrency(amount, currency)}</td>
                            <td>
                              <div className='d-flex flex-wrap gap-1'>
                                {/* promotionCode ödemelerinde "Cash" badge gösterilmez,
                                    sadece promo badge yeterli (aşağıda) */}
                                {sale.paymentType !== 'promotionCode' && (
                                  <span
                                    className={`badge ${sale.paymentType === 'cash' ? 'bg-info' : 'bg-primary'} text-white`}
                                  >
                                    {sale.paymentType === 'creditCard'
                                      ? 'Credit Card'
                                      : sale.paymentType === 'cash'
                                        ? 'Cash'
                                        : sale.paymentType || '-'}
                                  </span>
                                )}
                                {sale.orderId?.promoCode && (
                                  <span
                                    className={`badge ${
                                      sale.orderId?.promoDiscountType === 1
                                        ? 'bg-success text-white'
                                        : sale.orderId?.promoDiscountType === 2
                                          ? 'bg-secondary text-white'
                                          : 'bg-warning text-dark'
                                    }`}
                                    title={
                                      sale.orderId?.promoDiscountType === 1
                                        ? `%${sale.orderId?.promoDiscountValue || 0} indirim`
                                        : sale.orderId?.promoDiscountType === 2
                                          ? `${sale.orderId?.promoDiscountValue || 0} TL indirim`
                                          : 'Promosyon'
                                    }
                                  >
                                    <i
                                      className={`me-1 ${
                                        sale.orderId?.promoDiscountType === 1
                                          ? 'ri-percent-line'
                                          : sale.orderId?.promoDiscountType === 2
                                            ? 'ri-money-dollar-circle-line'
                                            : 'ri-coupon-line'
                                      }`}
                                    ></i>
                                    {sale.orderId.promoCode}
                                    {sale.orderId?.promoDiscountType === 1 &&
                                      sale.orderId?.promoDiscountValue && (
                                        <span className='ms-1'>
                                          (%{sale.orderId.promoDiscountValue})
                                        </span>
                                      )}
                                    {sale.orderId?.promoDiscountType === 2 &&
                                      sale.orderId?.promoDiscountValue && (
                                        <span className='ms-1'>
                                          ({sale.orderId.promoDiscountValue}₺)
                                        </span>
                                      )}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>{renderStatus(sale.status || sale.orderId?.status)}</td>
                            <td className='text-muted'>
                              {formatDate(sale.createdAt || sale.orderId?.createdAt)}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          {totalPages > 1 && (
            <Pagination
              perPageData={PERPAGE_DEFAULT}
              data={sales}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={totalPages}
            />
          )}
        </Container>
      </div>

      <style>{`
        .card-animate {
          transition: all 0.3s ease;
        }
        .card-animate:hover {
          transform: translateY(-5px);
          box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.1);
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </React.Fragment>
  )
}

export default Sales
